import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Loader2, PlugZap, RefreshCcw, Search, Star,
} from "lucide-react";

/* ------------------------------ API connection card ------------------------------ */
const ConnectionCard = ({ cfg, reload }) => {
  const [key, setKey] = useState("");
  const [time, setTime] = useState(cfg.fetch_time_ist || "07:00");
  const [enabled, setEnabled] = useState(cfg.enabled);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [fetching, setFetching] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const body = { enabled, fetch_time_ist: time };
      if (key.trim()) body.api_key = key.trim();
      await api.put("/google-ratings/config", body);
      toast.success("Google Places settings saved");
      setKey("");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/google-ratings/test");
      if (r.data.ok) {
        toast.success(`Connection OK — ${r.data.tested}: ${r.data.name ?? ""} ${r.data.rating ?? ""} ★ (${r.data.total_reviews ?? 0} reviews)`);
      } else {
        toast.error(r.data.error || "Test failed");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTesting(false);
    }
  };

  const fetchNow = async () => {
    setFetching(true);
    try {
      const r = await api.post("/google-ratings/fetch");
      toast.success(`Fetched ${r.data.fetched} · failed ${r.data.failed} · demo skipped ${r.data.skipped_demo}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setFetching(false);
    }
  };

  return (
    <Card data-testid="google-connection-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
          <PlugZap size={15} className="text-[hsl(var(--cx-gold))]" /> Places API connection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs">Google API key {cfg.api_key_set && (
              <span className="text-muted-foreground font-normal">— saved: {cfg.api_key_masked}</span>)}
            </Label>
            <Input type="password" value={key} onChange={(e) => setKey(e.target.value)}
              placeholder={cfg.api_key_set ? "Enter a new key to replace the saved one" : "Paste your Google Cloud API key (Places API New enabled)"}
              className="mt-1" data-testid="google-api-key-input" autoComplete="off" />
          </div>
          <div>
            <Label className="text-xs">Daily fetch time (IST)</Label>
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="mt-1 w-[130px]" data-testid="google-fetch-time-input" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="google-enabled-switch" />
          <span className="text-sm">Daily automatic fetch {enabled ? "enabled" : "disabled"}</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={save} disabled={saving} data-testid="google-config-save-button">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />} Save settings
          </Button>
          <Button variant="outline" onClick={test} disabled={testing || !cfg.api_key_set}
            data-testid="google-test-connection-button">
            {testing ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <CheckCircle2 size={14} className="mr-1.5" />}
            Test connection
          </Button>
          <Button variant="outline" onClick={fetchNow} disabled={fetching || !cfg.api_key_set}
            data-testid="google-fetch-all-button">
            {fetching ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <RefreshCcw size={14} className="mr-1.5" />}
            Fetch now (all)
          </Button>
        </div>
        <p className="text-[0.68rem] text-muted-foreground">
          Public listing data only (rating + review count + recent reviews) — no Google Business Profile login needed.
          The key never leaves the server. ~20 listings once daily stays inside Google's free monthly allowance.
          Review capture is best-effort: Google's API shares only the ~5 most recent reviews per listing per fetch,
          so some reviews (and 1–2★ alerts) can be missed between fetches.
        </p>
      </CardContent>
    </Card>
  );
};

/* ------------------------------ lookup dialog ------------------------------ */
const LookupDialog = ({ open, onOpenChange, onPick, keySet }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const search = async () => {
    setSearching(true);
    setResults(null);
    try {
      const r = await api.post("/google-ratings/lookup", { query });
      setResults(r.data.results);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSearching(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="google-lookup-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif font-normal">Find the Google listing</DialogTitle>
        </DialogHeader>
        {!keySet && (
          <p className="text-xs text-[hsl(var(--status-amber))]">
            Save the Google API key first — lookup searches the live Places API.
          </p>
        )}
        <div className="flex gap-2">
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "Incheon Kia Nettoor"' data-testid="google-lookup-input"
            onKeyDown={(e) => e.key === "Enter" && query.length >= 3 && search()} />
          <Button onClick={search} disabled={searching || query.length < 3 || !keySet}
            data-testid="google-lookup-search-button">
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          </Button>
        </div>
        {results && results.length === 0 && (
          <p className="text-sm text-muted-foreground">No listings found — try adding the city name.</p>
        )}
        {results?.length > 0 && (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {results.map((p) => (
              <button key={p.place_id}
                className="w-full text-left border rounded-md px-3 py-2 hover:bg-[hsl(var(--cx-surface))] transition-colors"
                data-testid={`google-lookup-result-${p.place_id}`}
                onClick={() => { onPick(p.place_id); onOpenChange(false); }}>
                <p className="text-sm font-medium">{p.name}</p>
                <p className="text-xs text-muted-foreground truncate">{p.address}</p>
                <p className="text-xs tabular mt-0.5">
                  {p.rating ?? "—"} ★ · {p.total_reviews ?? 0} reviews
                  <span className="text-muted-foreground"> · {p.place_id}</span>
                </p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------ listings mapping ------------------------------ */
const ListingRow = ({ outlet, keySet, onSaved, openLookup }) => {
  const gl = outlet.google_listings || {};
  const [sales, setSales] = useState(gl.sales_place_id || "");
  const [service, setService] = useState(gl.service_place_id || "");
  const [combined, setCombined] = useState(Boolean(gl.combined));
  const [saving, setSaving] = useState(false);
  const type = outlet.type || "Both";
  const isDemo = Boolean(gl.demo) && Boolean(gl.sales_place_id || gl.service_place_id);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/outlets/${outlet.id}/google-listings`, {
        sales_place_id: sales || null, service_place_id: service || null, combined,
      });
      toast.success(`${outlet.name} listings saved`);
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TableRow data-testid={`google-listing-row-${outlet.code}`}>
      <TableCell className="pl-4">
        <span className="font-medium">{outlet.name}</span>{" "}
        <span className="text-xs text-muted-foreground">{outlet.code}</span>
        <Badge variant="outline" className="ml-1.5 text-[0.6rem]">{type}</Badge>
        {isDemo && <Badge variant="outline" className="ml-1 text-[0.6rem] text-[hsl(var(--status-amber))] border-[hsl(var(--status-amber)/0.4)]">Demo</Badge>}
      </TableCell>
      <TableCell>
        {type !== "Service" ? (
          <div className="flex gap-1">
            <Input value={sales} onChange={(e) => { setSales(e.target.value); if (combined) setService(e.target.value); }}
              placeholder="Sales listing Place ID" className="h-8 text-xs min-w-[170px]"
              data-testid={`google-sales-pid-${outlet.code}`} />
            <Button variant="outline" size="sm" className="h-8 px-2"
              onClick={() => openLookup((pid) => { setSales(pid); if (combined) setService(pid); })}
              disabled={!keySet} title="Search Google" data-testid={`google-sales-lookup-${outlet.code}`}>
              <Search size={13} />
            </Button>
          </div>
        ) : <span className="text-xs text-muted-foreground">—</span>}
      </TableCell>
      <TableCell>
        {type !== "Sales" ? (
          <div className="flex gap-1">
            <Input value={service} onChange={(e) => setService(e.target.value)}
              placeholder="Service listing Place ID" className="h-8 text-xs min-w-[170px]"
              disabled={combined} data-testid={`google-service-pid-${outlet.code}`} />
            <Button variant="outline" size="sm" className="h-8 px-2"
              onClick={() => openLookup((pid) => setService(pid))}
              disabled={!keySet || combined} title="Search Google" data-testid={`google-service-lookup-${outlet.code}`}>
              <Search size={13} />
            </Button>
          </div>
        ) : <span className="text-xs text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-center">
        {type === "Both" ? (
          <Checkbox checked={combined}
            onCheckedChange={(v) => { setCombined(Boolean(v)); if (v) setService(sales); }}
            data-testid={`google-combined-${outlet.code}`} />
        ) : <span className="text-xs text-muted-foreground">n/a</span>}
      </TableCell>
      <TableCell className="text-right pr-4">
        <Button size="sm" variant="outline" className="h-8" onClick={save} disabled={saving}
          data-testid={`google-listing-save-${outlet.code}`}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : "Save"}
        </Button>
      </TableCell>
    </TableRow>
  );
};

/* ------------------------------ insight thresholds ------------------------------ */
const THRESHOLD_FIELDS = [
  ["closure_rate_pts", "Closure rate change (pts)"],
  ["opened_pct", "Opened volume change (%)"],
  ["red_rise_days", "Red rising (consecutive days)"],
  ["res_days", "Avg resolution change (days)"],
  ["rating_delta", "Google rating move (±)"],
  ["reopen_pct", "Reopen-rate warning (%)"],
  ["min_volume", "Min complaints for % rules"],
];

const ThresholdsCard = ({ thresholds, reload }) => {
  const [vals, setVals] = useState(thresholds);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/google-ratings/thresholds", vals);
      toast.success("Insight thresholds saved");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="insight-thresholds-card">
      <CardHeader className="pb-2">
        <CardTitle className="font-serif text-base font-normal">Trend Insight thresholds</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {THRESHOLD_FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-[0.68rem]">{label}</Label>
              <Input type="number" step="any" value={vals[k] ?? ""} className="mt-1 h-8"
                onChange={(e) => setVals((p) => ({ ...p, [k]: e.target.value === "" ? null : Number(e.target.value) }))}
                data-testid={`threshold-${k}-input`} />
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="thresholds-save-button">
          {saving && <Loader2 size={13} className="animate-spin mr-1.5" />} Save thresholds
        </Button>
        <p className="text-[0.68rem] text-muted-foreground">
          Insights only fire when a change crosses these limits — keeping every statement significant and verifiable.
        </p>
      </CardContent>
    </Card>
  );
};

/* ------------------------------ fetch log ------------------------------ */
const FetchLogCard = ({ log, flagged }) => (
  <Card data-testid="google-fetch-log-card">
    <CardHeader className="pb-2">
      <CardTitle className="font-serif text-base font-normal">Fetch log</CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {flagged.length > 0 && (
        <div className="mb-3 rounded-md border border-[hsl(var(--status-amber)/0.4)] bg-[hsl(var(--status-amber)/0.07)] px-3 py-2"
          data-testid="google-flagged-listings">
          <p className="text-xs font-semibold text-[hsl(var(--status-amber))] flex items-center gap-1.5">
            <AlertTriangle size={13} /> {flagged.length} listing{flagged.length !== 1 ? "s" : ""} failing 3+ consecutive days
          </p>
          <p className="text-[0.68rem] text-muted-foreground mt-0.5">
            {flagged.map((f) => `${f.function} · ${f.place_id} (${f.consecutive_fails} fails)`).join(" · ")}
          </p>
        </div>
      )}
      {log.length === 0 ? (
        <p className="text-sm text-muted-foreground py-3">No fetches yet — runs appear here after the first manual or scheduled fetch.</p>
      ) : (
        <div className="max-h-72 overflow-y-auto space-y-1">
          {log.map((l) => (
            <div key={l.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-[hsl(var(--cx-surface))]">
              {l.status === "run_summary" ? (
                <>
                  <RefreshCcw size={12} className="text-[hsl(var(--cx-navy))] shrink-0" />
                  <span className="tabular">{l.at_fmt}</span>
                  <span className="text-muted-foreground">{l.trigger} run — {l.fetched} fetched, {l.failed} failed, {l.skipped_demo} demo skipped</span>
                </>
              ) : (
                <>
                  <AlertTriangle size={12} className="text-[hsl(var(--cx-crimson))] shrink-0" />
                  <span className="tabular">{l.at_fmt}</span>
                  <span className="font-medium">{l.outlet_name} · {l.function}</span>
                  <span className="text-muted-foreground truncate">{l.error}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);

/* ------------------------------ main tab ------------------------------ */
export const GoogleRatingsTab = () => {
  const [cfg, setCfg] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [log, setLog] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [lookup, setLookup] = useState({ open: false, onPick: null });

  const load = useCallback(() => {
    api.get("/google-ratings/config").then((r) => setCfg(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/outlets").then((r) => setOutlets(r.data)).catch(() => {});
    api.get("/google-ratings/fetch-log")
      .then((r) => { setLog(r.data.log); setFlagged(r.data.flagged); })
      .catch(() => {});
  }, []);

  useEffect(load, [load]);

  if (!cfg) return <p className="text-sm text-muted-foreground py-6">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="settings-google-ratings-tab">
      <ConnectionCard cfg={cfg} reload={load} />

      <Card data-testid="google-listings-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-serif text-base font-normal flex items-center gap-1.5">
            <Star size={15} className="text-[hsl(var(--cx-gold))]" fill="currentColor" strokeWidth={0} />
            Location listings — Place ID mapping
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 px-0">
          <p className="px-5 pb-2 text-[0.68rem] text-muted-foreground">
            Use the search button to find each listing by name (e.g. "Incheon Kia Nettoor") — the Place ID fills in
            automatically. Locations with one combined Google listing: tick Combined to use the same listing for both functions.
          </p>
          <div className="overflow-x-auto">
            <Table className="cx-table min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-4">Location</TableHead>
                  <TableHead>Sales listing</TableHead>
                  <TableHead>Service listing</TableHead>
                  <TableHead className="text-center">Combined</TableHead>
                  <TableHead className="text-right pr-4" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {outlets.map((o) => (
                  <ListingRow key={o.id} outlet={o} keySet={cfg.api_key_set} onSaved={load}
                    openLookup={(onPick) => setLookup({ open: true, onPick })} />
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <ThresholdsCard thresholds={cfg.thresholds} reload={load} />
      <FetchLogCard log={log} flagged={flagged} />

      <LookupDialog open={lookup.open} keySet={cfg.api_key_set}
        onOpenChange={(v) => setLookup((p) => ({ ...p, open: v }))}
        onPick={(pid) => lookup.onPick && lookup.onPick(pid)} />
    </div>
  );
};
